import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { FaArrowRightFromBracket, FaCircleCheck, FaFilter, FaMagnifyingGlass, FaPlug, FaRotate } from 'react-icons/fa6';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useApp } from 'common/AppContext';

const TYPE_VARIANT = {
    'Bug': 'danger',
    'Issue': 'warning',
    'Product Backlog Item': 'success',
    'Task': 'secondary',
    'User Story': 'info'
};

const STATE_VARIANT = {
    Active: 'primary',
    Approved: 'info',
    Committed: 'info',
    Doing: 'primary',
    Done: 'success',
    New: 'secondary',
    Resolved: 'success'
};

// Filter state lives in URL query params so reloads + back-button work and
// links to a filtered view are shareable.
const FILTER_DEFAULTS = { areaPath: '', assignee: 'me', search: '', state: 'open' };

// How many work items to render per page client-side. Server returns up to
// 200; anything beyond that, narrow the filters.
const PAGE_SIZE = 25;

// Curated state options for the dropdown — the GMetrix workflow's only
// in-progress states, in the order they appear in the lifecycle. Closed/Done/
// Removed are deliberately omitted from the UI; they're still valid in the
// backend allowlist so the "Open" default (NOT IN those) keeps working.
const STATE_FILTER_OPTIONS = [
    'New',
    'Awaiting Development',
    'In Development',
    'Awaiting Code Review',
    'Awaiting QA',
    'QA'
];

function parseFilters(search) {

    const p = new URLSearchParams(search);
    return {
        areaPath: p.get('areaPath') || FILTER_DEFAULTS.areaPath,
        assignee: p.get('assignee') || FILTER_DEFAULTS.assignee,
        search: p.get('search') || FILTER_DEFAULTS.search,
        state: p.get('state') || FILTER_DEFAULTS.state
    };

}

function filtersToSearch(filters) {

    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {

        if (v && v !== FILTER_DEFAULTS[k]) p.set(k, v);

    });
    const s = p.toString();
    return s ? `?${s}` : '';

}

export const AzureDevOps = () => {

    const { api } = useApp();
    const history = useHistory();
    const location = useLocation();

    const [status, setStatus] = useState(null); // null = unknown, {connected: bool, ...} = known
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Mirror filters in component state for instant input feedback, but treat
    // the URL as the source of truth for "what's actually active".
    const activeFilters = useMemo(() => parseFilters(location.search), [location.search]);
    const [draftFilters, setDraftFilters] = useState(activeFilters);
    useEffect(() => {

        setDraftFilters(activeFilters);

    }, [activeFilters]);

    const loadStatus = useCallback(async () => {

        try {

            const data = await api.get('/api/azure-devops/status');
            setStatus(data || { connected: false });
            return data;

        } catch (e) {

            setError(e?.err || e?.message || String(e));
            return null;

        }

    }, [api]);

    const loadItems = useCallback(async filters => {

        const search = filtersToSearch(filters);

        try {

            setRefreshing(true);
            const data = await api.get(`/api/azure-devops/work-items${search}`);
            setItems(data?.items || []);
            setError(null);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setRefreshing(false);

        }

    }, [api]);

    // Single effect handles initial mount + every URL filter change. Status
    // check is cheap; items reload on every filter change is the point.
    useEffect(() => {

        let cancelled = false;

        (async () => {

            const s = await loadStatus();
            if (cancelled) return;
            if (s && s.connected) await loadItems(activeFilters);
            if (!cancelled) setLoading(false);

        })();

        return () => {

            cancelled = true;

        };

    }, [loadStatus, loadItems, activeFilters]);

    // Listen for the popup-flow signal from /api/azure-devops/callback. When
    // the user finishes consenting, the popup writes localStorage 'adoAuth'
    // and closes — we re-check status and pull items.
    useEffect(() => {

        const onStorage = async event => {

            if (event.key !== 'adoAuth') return;
            const s = await loadStatus();
            if (s && s.connected) await loadItems(activeFilters);

        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);

    }, [loadStatus, loadItems, activeFilters]);

    const applyFilters = next => {

        // Snap back to page 1 whenever filters change — a previous "page 4"
        // makes no sense once the result set has shifted.
        setPage(1);
        history.replace({ pathname: location.pathname, search: filtersToSearch(next) });

    };

    const resetFilters = () => applyFilters(FILTER_DEFAULTS);

    // Slice the live results into the current page.
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const visibleItems = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const startConnect = () => {

        const w = 600;
        const h = 720;
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;
        window.open(
            '/api/azure-devops/connect',
            'qa-hub-ado-connect',
            `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`
        );

    };

    const disconnect = async () => {

        if (!window.confirm('Disconnect your Azure DevOps account?')) return;
        try {

            await api.post('/api/azure-devops/disconnect');
            setStatus({ connected: false });
            setItems([]);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        }

    };

    if (loading)
        return (
            <Container className="py-4">
                <Spinner animation="border" />
            </Container>
        );

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h3 className="m-0">
                        Azure DevOps
                    </h3>
                    {status && status.connected && (
                        <div className="text-muted small mt-1">
                            <FaCircleCheck className="text-success me-1" />
                            Connected
                            {status.org && (
                                <>
                                    {' to '}
                                    <code>
                                        {status.org}
                                    </code>
                                </>
                            )}
                            {status.project && (
                                <>
                                    {' / '}
                                    <code>
                                        {status.project}
                                    </code>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {status && status.connected && (
                    <div className="d-flex gap-2">
                        <Button disabled={refreshing} onClick={() => loadItems(activeFilters)} size="sm" variant="outline-secondary">
                            <FaRotate className={refreshing ? 'me-1 fa-spin' : 'me-1'} />
                            Refresh
                        </Button>
                        <Button onClick={disconnect} size="sm" variant="outline-danger">
                            <FaArrowRightFromBracket className="me-1" />
                            Disconnect
                        </Button>
                    </div>
                )}
            </div>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            {!status?.connected ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <FaPlug className="text-primary mb-3" size={36} />
                        <h5>
                            Link your Azure DevOps account
                        </h5>
                        <p className="text-muted">
                            Sign in with your Microsoft account to view work items assigned to you. Generate
                            AI-powered test plans and launch them straight into the Feature Tester.
                        </p>
                        <Button onClick={startConnect} size="lg" variant="primary">
                            Connect Azure DevOps
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <>

                    <FilterBar
                        active={activeFilters}
                        draft={draftFilters}
                        onApply={() => applyFilters(draftFilters)}
                        onChange={setDraftFilters}
                        onReset={resetFilters}
                    />

                    {items.length === 0 && !refreshing ? (
                        <Alert variant="light">
                            No work items match the current filters.
                            {!filtersAreDefault(activeFilters) && (
                                <>
                                    {' '}
                                    <Button onClick={resetFilters} size="sm" variant="link">
                                        Reset filters
                                    </Button>
                                </>
                            )}
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-0">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>
                                                Type
                                            </th>
                                            <th>
                                                ID
                                            </th>
                                            <th>
                                                Title
                                            </th>
                                            <th>
                                                State
                                            </th>
                                            <th>
                                                Assignee
                                            </th>
                                            <th>
                                                Area path
                                            </th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleItems.map(item => (
                                            <tr key={item.id}>
                                                <td>
                                                    <Badge bg={TYPE_VARIANT[item.type] || 'secondary'}>
                                                        {item.type}
                                                    </Badge>
                                                </td>
                                                <td className="text-muted small">
                                                    #
                                                    {item.id}
                                                </td>
                                                <td>
                                                    <Link className="text-decoration-none" to={`/azure-devops/${item.id}`}>
                                                        {item.title}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <Badge bg={STATE_VARIANT[item.state] || 'secondary'}>
                                                        {item.state}
                                                    </Badge>
                                                </td>
                                                <td className="small">
                                                    {item.assignedTo || (
                                                        <span className="text-muted">
                                                            unassigned
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-muted small">
                                                    {item.areaPath}
                                                </td>
                                                <td>
                                                    <Link className="btn btn-sm btn-outline-primary" to={`/azure-devops/${item.id}`}>
                                                        Open
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {items.length > PAGE_SIZE && (
                        <PaginationBar
                            onPageChange={setPage}
                            page={safePage}
                            pageSize={PAGE_SIZE}
                            total={items.length}
                            totalPages={totalPages}
                        />
                    )}

                </>
            )}

        </Container>

    );

};

const filtersAreDefault = f => (
    f.assignee === FILTER_DEFAULTS.assignee
    && f.state === FILTER_DEFAULTS.state
    && (f.areaPath || '') === FILTER_DEFAULTS.areaPath
    && (f.search || '') === FILTER_DEFAULTS.search
);

/**
 * Filter row above the work-items table. Inputs are controlled (`draft`)
 * but only mutate the URL (and trigger a refetch) when the user clicks
 * Apply or presses Enter on the area-path field. Reset returns to the
 * "my open tickets" defaults.
 */
const FilterBar = ({ active, draft, onApply, onChange, onReset }) => {

    const dirty = (
        active.assignee !== draft.assignee
        || active.state !== draft.state
        || (active.areaPath || '') !== (draft.areaPath || '')
        || (active.search || '') !== (draft.search || '')
    );

    return (
        <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="py-2 px-3">
                <Form
                    onSubmit={e => {

                        e.preventDefault();
                        onApply();

                    }}
                >
                    <Row className="g-2 mb-2">
                        <Col>
                            <InputGroup size="sm">
                                <InputGroup.Text>
                                    <FaMagnifyingGlass size={11} />
                                </InputGroup.Text>
                                <Form.Control
                                    onChange={e => onChange({ ...draft, search: e.target.value })}
                                    placeholder="Search work item titles…"
                                    value={draft.search || ''}
                                />
                                {draft.search && (
                                    <Button
                                        onClick={() => onChange({ ...draft, search: '' })}
                                        type="button"
                                        variant="outline-secondary"
                                    >
                                        ×
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="g-2 align-items-end">

                        <Col md={3}>
                            <Form.Label className="small text-muted mb-1">
                                <FaFilter className="me-1" size={11} />
                                Assignee
                            </Form.Label>
                            <Form.Select
                                onChange={e => onChange({ ...draft, assignee: e.target.value })}
                                size="sm"
                                value={draft.assignee}
                            >
                                <option value="me">
                                    Mine
                                </option>
                                <option value="anyone">
                                    Anyone
                                </option>
                                <option value="unassigned">
                                    Unassigned
                                </option>
                            </Form.Select>
                        </Col>

                        <Col md={3}>
                            <Form.Label className="small text-muted mb-1">
                                State
                            </Form.Label>
                            <Form.Select
                                onChange={e => onChange({ ...draft, state: e.target.value })}
                                size="sm"
                                value={draft.state}
                            >
                                <option value="open">
                                    Open (default)
                                </option>
                                <option value="all">
                                    All states
                                </option>
                                <option disabled>
                                    ──────────
                                </option>
                                {STATE_FILTER_OPTIONS.map(s => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">
                                Area path contains
                            </Form.Label>
                            <Form.Control
                                onChange={e => onChange({ ...draft, areaPath: e.target.value })}
                                placeholder="e.g. LMS\Student"
                                size="sm"
                                value={draft.areaPath || ''}
                            />
                        </Col>

                        <Col md={2}>
                            <div className="d-flex gap-2">
                                <Button
                                    disabled={!dirty}
                                    size="sm"
                                    type="submit"
                                    variant="primary"
                                >
                                    Apply
                                </Button>
                                {!filtersAreDefault(active) && (
                                    <Button
                                        onClick={onReset}
                                        size="sm"
                                        type="button"
                                        variant="outline-secondary"
                                    >
                                        Reset
                                    </Button>
                                )}
                            </div>
                        </Col>

                    </Row>
                </Form>
            </Card.Body>
        </Card>
    );

};

/**
 * Compact prev/next + numbered pagination footer. Renders at most 7 page
 * buttons at a time with first/last anchors and an ellipsis when there's
 * more page distance than will fit. Hidden entirely when total ≤ pageSize
 * (caller's responsibility — we render unconditionally here).
 */
const PaginationBar = ({ onPageChange, page, pageSize, total, totalPages }) => {

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);

    // Build the visible page-number window. Always show first + last; show up
    // to 3 around the current page; ellipsis when there's a gap.
    const visible = [];
    const window = 1;
    for (let p = 1; p <= totalPages; p++) {

        const inWindow = Math.abs(p - page) <= window;
        const isAnchor = p === 1 || p === totalPages;
        if (inWindow || isAnchor) visible.push(p);

    }

    const items = [];
    for (let i = 0; i < visible.length; i++) {

        const p = visible[i];
        if (i > 0 && p - visible[i - 1] > 1) items.push({ ellipsis: true, key: `gap-${p}` });
        items.push({ key: `p-${p}`, page: p });

    }

    return (
        <div className="d-flex align-items-center justify-content-between mt-3">

            <div className="text-muted small">
                Showing
                {' '}
                {start}
                –
                {end}
                {' '}
                of
                {' '}
                {total}
            </div>

            <Pagination className="m-0" size="sm">
                <Pagination.Prev
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                />
                {items.map(it => (
                    it.ellipsis
                        ? <Pagination.Ellipsis disabled key={it.key} />
                        : (
                            <Pagination.Item
                                active={it.page === page}
                                key={it.key}
                                onClick={() => onPageChange(it.page)}
                            >
                                {it.page}
                            </Pagination.Item>
                        )
                ))}
                <Pagination.Next
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(page + 1)}
                />
            </Pagination>

        </div>
    );

};
